import { Alert, Box, Button, Collapse, Flex, FormControl, FormLabel, Icon, List, ListItem, Stack, Text, useDisclosure, useToast } from '@chakra-ui/react'
import type { ChangePasswordRequest, ModelState } from 'BrokerTsLib/src/dtos'
import { BadRequestError } from 'BrokerTsLib/src/dtos'
import { useChangePassword, useGetPasswordRules } from 'BrokerTsLib/src/hooks/useAccount'
import { getPasswordRules } from 'BrokerTsLib/src/hooks/useResetPassword'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import * as yup from 'yup'


import { faKey } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PasswordGenerator } from 'BrokerTsLib/src/components/PasswordGenerator'
import type { ReactNode } from 'react'
import { InputGroup } from '../../../../components/InputGroup'

const getChangePasswordSchema = (passwordRules: ReturnType<typeof getPasswordRules>) => {
  let newPasswordSchema = yup.string().required('Zadejte nové heslo')

  for (const rule of passwordRules) {
    newPasswordSchema = newPasswordSchema.test(rule.key, rule.name, rule.validator)
  }

  return yup.object().shape({
    oldPassword: yup.string().required('Zadejte aktuální heslo'),
    newPassword: newPasswordSchema.test(
      'differentFromOld',
      'Nové heslo musí být odlišné od starého',
      function (value) {
        return value !== this.parent.oldPassword
      }
    ),
    confirmPassword: yup.string()
      .required('Zadejte potvrzení hesla')
      .oneOf([yup.ref('newPassword'), ''], 'Hesla se neshodují')
  })
}

export const ChangePasswordForm = ({ buttonLeft, onPasswordChanged }: { buttonLeft?: ReactNode, onPasswordChanged?: () => void }) => {
  const toast = useToast()

  const { data: passwordRulesData, isLoading: isLoadingRules } = useGetPasswordRules()
  const passwordRules = getPasswordRules(passwordRulesData?.passwordRules)

  const form = useForm<ChangePasswordRequest>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    resolver: passwordRules.length > 0 ? yupResolver(getChangePasswordSchema(passwordRules)) as any : undefined,
  })
  const { register, handleSubmit, setError, formState: { errors }, setValue } = form

  const { mutateAsync: changePassword, isPending } = useChangePassword()
  const submitHandler = async (data: ChangePasswordRequest) => {
    try {
      await changePassword(data)

      toast({
        title: 'Heslo bylo změněno',
        status: 'success'
      })

      onPasswordChanged?.()
    }
    catch (e: any) {
      // Handle 400 Bad Request errors (validation errors from server)
      if (e?.response?.status === 400 && e?.response?.data) {
        setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<ChangePasswordRequest>))
      }
      // Handle 401 Unauthorized errors
      else if (e?.response?.status === 401) {
        setError('root', {
          type: 'manual',
          message: 'Nesprávné uživatelské jméno nebo heslo'
        })
      }
      // Handle network errors
      else if (!e?.response) {
        setError('root', {
          type: 'manual',
          message: 'Chyba připojení k serveru. Zkuste to prosím později.'
        })
      }
      // Handle other errors
      else {
        setError('root', {
          type: 'manual',
          message: 'Došlo k neočekávané chybě. Zkuste to prosím znovu.'
        })
      }
    }
  }
  
  const { isOpen, onToggle } = useDisclosure()
  const handlePasswordGenerated = (password: string) => {
    setValue('newPassword', password)
    setValue('confirmPassword', password, { shouldValidate: true })
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Stack gap={6}>
          <FormControl isInvalid={!!errors.oldPassword}>
            <FormLabel>Aktuální heslo</FormLabel>

            <InputGroup
              id="oldPassword"
              type="password"
              placeholder="Aktuální heslo"
              errors={errors.oldPassword}
              autoComplete='current-password'
              {...register('oldPassword')}
            />
          </FormControl>
          
          <Box>
            <Button
              variant="link"
              colorScheme="blue"
              onClick={onToggle}
              mb={2}
              size="sm"
            >
              <Icon as={FontAwesomeIcon} icon={faKey} />
              {isOpen ? 'Skrýt generátor hesla' : 'Použít generátor hesla'}
            </Button>
            <Collapse in={isOpen} animateOpacity>
              <Box p={4} borderWidth={1} borderRadius="md" mb={4} boxShadow="sm">
                <PasswordGenerator
                  rules={passwordRulesData?.passwordRules || {
                    minLenght: 8,
                    requireDigit: true,
                    requireLowercase: true,
                    requireUppercase: true,
                    requireNonLetterOrDigit: false
                  }}
                  onPasswordGenerated={handlePasswordGenerated}
                  showValidation={false}
                  autoGenerate={false}
                />
              </Box>
            </Collapse>
          </Box>

          <FormControl isInvalid={!!errors.newPassword}>
            <FormLabel>Nové heslo</FormLabel>

            <InputGroup
              id="newPassword"
              type="password"
              placeholder="Nové heslo"
              autoComplete='new-password'
              errors={errors.newPassword}
              {...register('newPassword')}
              aria-describedby='password-constraints'
            />
          </FormControl>

          {passwordRules.length > 0 && (
            <Stack spacing={2}>
              <Text fontWeight="medium" >
                Pravidla pro heslo:
              </Text>
              <List sx={{ fontSize: 'md', fontWeight: 'normal' }} listStyleType='disc' id="password-constraints">
                {passwordRules.map(rule => (
                  <ListItem key={rule.key}>{rule.name}</ListItem>
                ))}
              </List>
            </Stack>
          )}

          <FormControl isInvalid={!!errors.confirmPassword}>
            <FormLabel>Potvrzení nového hesla</FormLabel>

            <InputGroup
              id="confirmPassword"
              type="password"
              placeholder="Potvrzení nového hesla"
              autoComplete='new-password'
              errors={errors.confirmPassword}
              {...register('confirmPassword')}
            />
          </FormControl>

          {errors.root && (
            <Alert status='error'>
              {errors.root.message}
            </Alert>
          )}

          <Flex justifyContent='space-between'>
            {buttonLeft}
            <Button ml={'auto'} type="submit" colorScheme="primary" isDisabled={!form.formState.isValid || isLoadingRules} isLoading={isPending || isLoadingRules}>Změnit heslo</Button>
          </Flex>
        </Stack>
      </form>
    </FormProvider>
  )
}